import {Store} from "@reduxjs/toolkit"
import logger from "@next/core-logger"
import {getAutocompleteData} from "../../api/autocomplete"
import {AutoCompleteInitial, AutoCompleteStateModel} from "../../models/autocomplete"
import {showAutoComplete, showRecentSearch} from "../search"
import {SettingsSdkKeys} from "../../models/settings"

export const SET_LOADING = "SET_LOADING"
export const SET_AUTO_COMPLETE_DATA = "SET_AUTO_COMPLETE_DATA"
export const SET_PARAMETER_VALUES = "SET_PARAMETER_VALUES"
export const CLEAR_SUGGESTIONS = "CLEAR_SUGGESTIONS"

export const BR_UID_2 = "_br_uid_2"

export type AutoCompleteDuckState = AutoCompleteStateModel

export const initialState: AutoCompleteStateModel = {
    parameters: {
        apiUrl: "",
        accountId: "",
        domainKey: "",
        authKey: "",
        territory: "",
    },
    q: "",
    suggestions: null,
    isLoading: false,
    numFound: 0,
    products: null,
}
interface Actions {
    type: typeof SET_AUTO_COMPLETE_DATA | typeof SET_PARAMETER_VALUES | typeof CLEAR_SUGGESTIONS | typeof SET_LOADING
    payload: AutoCompleteInitial | boolean | any
}

export const setAutoCompleteData = (payload: AutoCompleteInitial): Actions => ({
    type: SET_AUTO_COMPLETE_DATA,
    payload,
})

export const clearSuggestions = () => ({
    type: CLEAR_SUGGESTIONS,
})

export const setIsLoading = (payload: boolean): Actions => ({
    type: SET_LOADING,
    payload,
})

type BloomReachConfig = {
    apiUrl?: string
    accountId?: string
    domainKey?: string
    authKey?: string
    territory?: string
}

const isBloomReachConfigValid = ({apiUrl, accountId, domainKey}: BloomReachConfig) => {
    return apiUrl?.length && accountId?.length && domainKey?.length
}

export const getAutoCompleteThunk =
    (searchValue: number | string, uid2: string, territoryVal: string, updateProductsOnly = false) =>
    async (dispatch: any, getState) => {
        try {
            const state = getState()
            const isAutocompleteEnabled = state.settings.autocompleteEnabled
            const bloomReachConfig: BloomReachConfig = state.settings.bloomReachConfig as BloomReachConfig

            if (isAutocompleteEnabled && isBloomReachConfigValid(bloomReachConfig)) {
                const {apiUrl, accountId, domainKey, authKey = "", territory = ""} = bloomReachConfig
                const localTerritory =
                    typeof territory === undefined || territory === "" ? territoryVal : (territory as string)
                dispatch(setIsLoading(true))
                dispatch(showRecentSearch(false))
                dispatch(showAutoComplete(true))

                const {autocomplete} = getState()
                const data = await getAutocompleteData(
                    apiUrl as string,
                    searchValue,
                    accountId as string,
                    domainKey as string,
                    authKey as string,
                    uid2,
                    localTerritory,
                )

                if (updateProductsOnly) {
                    dispatch({
                        type: SET_AUTO_COMPLETE_DATA,
                        payload: {
                            response: {
                                ...autocomplete,
                                products: data.response.products,
                                q: data.response.q,
                            },
                        },
                    })
                } else {
                    dispatch({
                        type: SET_AUTO_COMPLETE_DATA,
                        payload: {
                            ...data,
                            response: {
                                ...data.response,
                                q:
                                    data &&
                                    data.response &&
                                    data.response.suggestions &&
                                    data.response.suggestions.length > 0
                                        ? data.response.suggestions[0].q
                                        : data.response.q,
                            },
                        },
                    })
                }
                dispatch(setIsLoading(false))
            } else {
                dispatch(showRecentSearch(true))
                dispatch(showAutoComplete(false))
            }
        } catch (e) {
            logger.error(e)
            dispatch(showAutoComplete(false))
            dispatch({
                type: SET_AUTO_COMPLETE_DATA,
                payload: initialState,
            })
            dispatch(setIsLoading(false))
        }
    }

export const setAutoCompleteParameters = (store: Store, configuration: any) => {
    const payload = {
        territory:
            configuration[SettingsSdkKeys.bloomreachViewId]?.Value.toLowerCase() ||
            configuration[SettingsSdkKeys.territory].Value.toLowerCase(),
    }

    store.dispatch({
        type: SET_PARAMETER_VALUES,
        payload,
    })
}

const reducer = (state: AutoCompleteStateModel = initialState, action): AutoCompleteStateModel => {
    const {payload} = action
    switch (action.type) {
        case SET_LOADING:
            return {
                ...state,
                isLoading: action.payload,
            }
        case SET_AUTO_COMPLETE_DATA:
            return {
                ...state,
                q: payload?.response?.q || "",
                suggestions: payload?.response?.suggestions || [],
                numFound: payload?.response?.numFound || 0,
                products: payload?.response?.products || [],
            }
        case CLEAR_SUGGESTIONS:
            return {
                ...state,
                q: "",
                suggestions: [],
                numFound: 0,
            }
        case SET_PARAMETER_VALUES:
            return {...state, parameters: payload}
        default:
            return state
    }
}

export default reducer
